import React, { memo, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import Draggable from 'react-draggable';
import classNames from 'classnames';

import {
  useSharedPusher,
  CHANNEL_SCREEN_POP,
  CHANNEL_SCREEN_POP_ENDED,
  CHANNEL_SCREEN_POP_STARTED,
} from 'src/hooks/useSharedPusher';

import { getChannels } from '../../actions/dialPadPusher';
import { finishCall } from '../../actions/dialPadCall';
import Button from '../../components/helpers/Button';

import style from './style.module.css';

const DEFAULT_POSITION = {
  x: Math.floor((window.innerWidth - 600) / 2),
  y: Math.floor((window.innerHeight - 250) / 2),
};

const DialPadPusher = ({ children, user, google2FaStatus, screenPopChannel, getChannels, finishCall }) => {
  const [isOpen, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const { subscribe, unsubscribe } = useSharedPusher(user);

  const draggableRef = useRef(null);

  const handleClose = useCallback(() => {
    setOpen(false);
    setContacts([]);
    finishCall();
  }, []);

  useEffect(() => {
    getChannels();
  }, []);

  useEffect(() => {
    if (google2FaStatus && google2FaStatus.get('isGoogle2FAPassed')) {
      getChannels();
    }
  }, [google2FaStatus]);

  useEffect(() => {
    if (!screenPopChannel) return;

    const channel = screenPopChannel.get('name');

    const handleOpenModal = ({ data }) => {
      setOpen(true);
      setContacts(data.contacts);
    };

    const handleCloseModal = () => {
      setOpen(false);
      setContacts([]);
    };

    subscribe(channel, CHANNEL_SCREEN_POP_STARTED, handleOpenModal);
    subscribe(channel, CHANNEL_SCREEN_POP_ENDED, handleCloseModal);

    return () => {
      unsubscribe(channel, CHANNEL_SCREEN_POP_STARTED);
      unsubscribe(channel, CHANNEL_SCREEN_POP_ENDED);
    };
  }, [screenPopChannel]);

  const modal = useMemo(() => {
    const className = isOpen ? 'move modal fade in' : 'move modal fade';

    return (
      <>
        {isOpen && <div className={classNames(style.backdrop, 'modal-backdrop fade in')} />}
        <div
          aria-labelledby="callingContactModalLabel"
          className={className}
          id="callingContactPopup"
          role="dialog"
          style={{ display: isOpen ? 'block' : 'none' }}
          tabIndex="-1"
        >
          <Draggable
            axis="both"
            bounds="parent"
            defaultPosition={DEFAULT_POSITION}
            handle=".handle-draggable"
            nodeRef={draggableRef}
            position={null}
          >
            <div ref={draggableRef} className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header truncate-text handle-draggable">
                  <button aria-label="Close" className="close" onClick={handleClose} tabIndex="-1" type="button">
                    <span aria-hidden="true">×</span>
                  </button>
                  <h4 className="modal-title">Incoming Call...</h4>
                </div>

                <div className="modal-body">
                  <ul className="list-unstyled">
                    {contacts?.map(contact => {
                      const contactInfo =
                        contact.source === 'dialpad' ? (
                          <span>{contact.contact_name}</span>
                        ) : (
                          <>
                            <a href={`/contact/${contact.contact_id}`} rel="noopener noreferrer" target="_blank">
                              {contact.contact_name}
                            </a>
                            , {contact.title}
                            &nbsp;
                            <a href={`/company/${contact.target_id}`} rel="noopener noreferrer" target="_blank">
                              {contact.legal_name}
                            </a>
                          </>
                        );

                      return (
                        <li key={contact.contact_id} className="pt3 pb3 font-size-18">
                          <i className="fa fa-phone mr5" />
                          {contactInfo}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="modal-footer">
                  <Button onClick={handleClose}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </Draggable>
        </div>
      </>
    );
  }, [isOpen, contacts]);

  return (
    <>
      {children}
      {modal}
    </>
  );
};

const mapStateToProps = (state, props) => ({
  ...props,
  user: state.auth.get('user', null),
  google2FaStatus: state.auth.get('google2FaStatus'),
  screenPopChannel: state.dialPadPusher.get('data', null)?.find(channel => channel.get('type') === CHANNEL_SCREEN_POP),
});

export default memo(
  connect(mapStateToProps, {
    getChannels,
    finishCall,
  })(DialPadPusher),
);
